// Centralized form configuration
// This file manages all form definitions, making it easy to add new forms or modify existing ones

import OfferLetterForm from './forms/OfferLetterForm';
import FoodHandlersCertForm from './forms/FoodHandlersCertForm';
import PersonalInformationForm from './forms/PersonalInformationForm';
import EmergencyContactForm from './forms/EmergencyContactForm';
import HandbookAgreementForm from './forms/HandbookAgreementForm';
import DirectDepositForm from './forms/DirectDepositForm';
import NYIT2104Form from './forms/NYIT2104Form';
import W4Form from './forms/W4Form';
import I9FormUpload from './forms/I9FormUpload';
import WageRateNoticeForm from './forms/WageRateNoticeForm';
import AtWillAgreementForm from './forms/AtWillEmploymentForm';
import HarassmentTrainingForm from './forms/HarassmentTrainingForm';

// Form type constants
export const FORM_TYPES = {
  DOCUMENT_UPLOAD: 'document_upload',
  DATA_COLLECTION: 'data_collection',
  SIGNATURE_REQUIRED: 'signature_required',
  MULTI_STEP: 'multi_step'
};

// Form status constants
export const FORM_STATUS = {
  INCOMPLETE: 'Incomplete',
  IN_PROGRESS: 'In Progress',
  PENDING_SIGNATURE: 'Pending Signature',
  COMPLETED: 'Completed',
  EXPIRED: 'Expired'
};

// Form configuration object
export const FORM_CONFIG = {
  '1': {
    id: '1',
    name: 'Offer Letter',
    component: OfferLetterForm,
    type: FORM_TYPES.SIGNATURE_REQUIRED,
    category: 'Onboarding',
    description: 'Initial employment offer letter requiring signature',
    required: true,
    order: 1,
    validationRules: {
      requiresSignature: true,
      expiresAfterDays: 7
    }
  },
  '11': {
    id: '11',
    name: 'Food Handlers Certification',
    component: FoodHandlersCertForm,
    type: FORM_TYPES.DOCUMENT_UPLOAD,
    category: 'Certifications',
    description: 'Food safety certification required for food service employees',
    required: false, // May be conditional based on position
    order: 2,
    validationRules: {
      acceptedFormats: ['.pdf', '.jpg', '.jpeg', '.png'],
      maxFileSize: 5 * 1024 * 1024, // 5MB
      requiresCertification: true
    },
    endpoints: {
      upload: '/api/employees/:employeeId/food-handlers-cert',
      retrieve: '/api/employees/:employeeId/food-handlers-cert',
      delete: '/api/employees/:employeeId/food-handlers-cert'
    }
  },
  '12': {
    id: '12',
    name: 'Form I-9 Employment Eligibility Verification',
    component: I9FormUpload,
    type: FORM_TYPES.MULTI_STEP,
    category: 'Government Forms',
    description: 'Federal form to verify employment eligibility',
    required: true,
    order: 3,
    validationRules: {
      requiresDocumentUpload: true,
      requiresEmployerVerification: true,
      expiresAfterDays: 3
    }
  },
  '4': {
    id: '4',
    name: 'At-Will Employment Agreement',
    component: AtWillAgreementForm,
    type: FORM_TYPES.SIGNATURE_REQUIRED,
    category: 'Employment Agreement',
    description: 'At-will employment agreement',
    required: true,
    order: 11,
    validationRules: {
      requiresSignature: true
    }
  },
  '2': {
    id: '2',
    name: 'Personal Information',
    component: PersonalInformationForm,
    type: FORM_TYPES.DATA_COLLECTION,
    category: 'Employee Information',
    description: 'Basic personal information and contact details',
    required: true,
    order: 4,
    validationRules: {
      requiredFields: ['address', 'phone', 'emergencyContact']
    }
  },
  '3': {
    id: '3',
    name: 'Emergency Contact',
    component: EmergencyContactForm,
    type: FORM_TYPES.DATA_COLLECTION,
    category: 'Employee Information',
    description: 'Emergency contact information',
    required: true,
    order: 5,
    validationRules: {
      requiredFields: ['name', 'relationship', 'phone']
    }
  },
  '5': {
    id: '5',
    name: 'Employee Handbook Agreement',
    component: HandbookAgreementForm,
    type: FORM_TYPES.SIGNATURE_REQUIRED,
    category: 'Company Policies',
    description: 'Acknowledgment of receipt and understanding of employee handbook',
    required: true,
    order: 6,
    validationRules: {
      requiresSignature: true,
      requiresAcknowledgment: true
    }
  },
  '6': {
    id: '6',
    name: 'Direct Deposit Form',
    component: DirectDepositForm,
    type: FORM_TYPES.DATA_COLLECTION,
    category: 'Payroll',
    description: 'Bank account information for direct deposit',
    required: false,
    order: 7,
    validationRules: {
      requiredFields: ['bankName', 'accountNumber', 'routingNumber'],
      sensitiveData: true
    }
  },
  '7': {
    id: '7',
    name: 'NY IT-2104 Form',
    component: NYIT2104Form,
    type: FORM_TYPES.DATA_COLLECTION,
    category: 'Tax Forms',
    description: 'New York State tax withholding form',
    required: true,
    order: 8,
    validationRules: {
      stateSpecific: 'NY'
    }
  },
  '9': {
    id: '9',
    name: 'W-4 Form',
    component: W4Form,
    type: FORM_TYPES.DATA_COLLECTION,
    category: 'Tax Forms',
    description: 'Federal tax withholding form',
    required: true,
    order: 9,
    validationRules: {
      requiresCalculation: true
    }
  },
  '8': {
    id: '8',
    name: 'Wage Rate Notice',
    component: WageRateNoticeForm,
    type: FORM_TYPES.SIGNATURE_REQUIRED,
    category: 'Payroll',
    description: 'New York wage rate notification',
    required: true,
    order: 10,
    validationRules: {
      stateSpecific: 'NY',
      requiresSignature: true
    }
  },
  '10': {
    id: '10',
    name: 'Harassment Training',
    component: HarassmentTrainingForm,
    type: FORM_TYPES.SIGNATURE_REQUIRED,
    category: 'Training',
    description: 'Sexual harassment prevention training acknowledgment',
    required: true,
    order: 12,
    validationRules: {
      requiresSignature: true
    }
  }
};

// Helper functions
export const getFormById = (formId) => FORM_CONFIG[formId] || null;

export const getFormsByCategory = (category) => {
  return Object.values(FORM_CONFIG).filter(form => form.category === category);
};

export const getRequiredForms = () => {
  return Object.values(FORM_CONFIG).filter(form => form.required);
};

export const getFormsByType = (type) => {
  return Object.values(FORM_CONFIG).filter(form => form.type === type);
};

export const getFormComponent = (formId) => {
  const form = getFormById(formId);
  return form ? form.component : null;
};

// Validation helpers
export const validateFileUpload = (file, formId) => {
  const form = getFormById(formId);
  if (!form || form.type !== FORM_TYPES.DOCUMENT_UPLOAD) {
    return { valid: true };
  }

  const rules = form.validationRules;
  
  // Check file format
  if (rules.acceptedFormats) {
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!rules.acceptedFormats.includes(fileExt)) {
      return {
        valid: false,
        error: `Invalid file format. Accepted formats: ${rules.acceptedFormats.join(', ')}`
      };
    }
  }

  // Check file size
  if (rules.maxFileSize && file.size > rules.maxFileSize) {
    const maxSizeMB = rules.maxFileSize / (1024 * 1024);
    return {
      valid: false,
      error: `File size must be less than ${maxSizeMB}MB`
    };
  }

  return { valid: true };
};

// Get forms that need employee action
export const getIncompleteForms = (formsList) => {
  return formsList.filter(form => !form.isCompleted);
};

// Get forms by their completion status
export const getFormsByStatus = (formsList, status) => {
  return formsList.filter(form => {
    if (status === FORM_STATUS.COMPLETED) {
      return form.isCompleted;
    } else if (status === FORM_STATUS.INCOMPLETE) {
      return !form.isCompleted;
    }
    return form.documentStatus === status;
  });
};

// Sort forms by their configured order
export const sortFormsByOrder = (formsList) => {
  return formsList.sort((a, b) => {
    const configA = getFormById(a.formId);
    const configB = getFormById(b.formId);
    return (configA?.order || 999) - (configB?.order || 999);
  });
};

export default FORM_CONFIG;